import React, { createContext, useContext, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { api, setCsrf } from './api';
import Dashboard from './pages/Dashboard';
import Workspace from './pages/Workspace';
import MapPage from './pages/MapPage';
import ParcelPage from './pages/ParcelPage';
import SourcesPage from './pages/SourcesPage';
import ChecksPage from './pages/ChecksPage';
import PracticesPage from './pages/PracticesPage';
import DossiersPage from './pages/DossiersPage';
import AccessPage from './pages/AccessPage';
import LocalEnginePage from './pages/LocalEnginePage';
import { hybridEnabled, signup } from './hybridApi';
import './App.css';

const Auth = createContext(null);
export const useAuth = () => useContext(Auth);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api('/auth/me').then((data) => { setUser(data.user); setCsrf(data.csrf); }).catch(() => {}).finally(() => setLoading(false));
    const expired = () => { setUser(null); setCsrf(''); };
    window.addEventListener('agrinext:session-expired', expired);
    return () => window.removeEventListener('agrinext:session-expired', expired);
  }, []);
  const login = async (email, password) => {
    const data = await api('/auth/login', { method: 'POST', body: { email, password } });
    setCsrf(data.csrf);
    setUser(data.user);
  };
  const logout = async () => {
    try { await api('/auth/logout', { method: 'POST' }); } finally { setUser(null); setCsrf(''); }
  };
  return <Auth.Provider value={{ user, loading, login, logout }}>{children}</Auth.Provider>;
}

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen" data-testid="session-loading">Caricamento area riservata…</div>;
  return user ? <Outlet /> : <Navigate to="/login" replace />;
}

function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/progetti" replace />;
  async function submit(event) {
    event.preventDefault(); setBusy(true); setError('');
    try { await login(email, password); navigate('/progetti', { replace: true }); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  return <main className="login-page"><div className="login-band"><div className="wordmark wordmark-light">AGRINEXT <span>GEOINT</span></div><div className="login-headline"><span className="eyebrow">INTELLIGENCE TERRITORIALE</span><h1>Il territorio,<br/><em>con le prove.</em></h1><p>Progetti, cartografia e fonti nello stesso spazio di lavoro riservato.</p></div><div className="login-foot">ANALISI DI SITO / ABRUZZO · ITALIA</div></div><div className="login-form-side"><div className="login-form"><span className="eyebrow">AREA RISERVATA</span><h2>Accedi al tuo spazio</h2><p className="muted">Le informazioni dei clienti sono accessibili solo agli utenti assegnati.</p><form onSubmit={submit}><label htmlFor="email">Indirizzo email</label><input id="email" data-testid="login-email-input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required /><label htmlFor="password">Password</label><input id="password" data-testid="login-password-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />{error && <p className="form-error" role="alert" data-testid="login-error">{error}</p>}<button type="submit" className="action-primary" disabled={busy} data-testid="login-submit-button">{busy ? 'Accesso in corso…' : 'Accedi allo spazio'} <span aria-hidden="true">↗</span></button></form>{hybridEnabled && <p><a href="/attiva">Primo accesso con email invitata →</a></p>}</div></div></main>;
}

function Invite() {
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const token = decodeURIComponent(window.location.hash.slice(1));
  const submit = async (event) => {
    event.preventDefault();
    if(hybridEnabled) {
      try { await signup(email,password); setPassword(''); setMessage('Controlla la tua email per confermare l’accesso, poi accedi. Sono ammessi solo gli indirizzi invitati.'); }
      catch(e) { setMessage(e.message); }
      return;
    }
    try { await api('/invitations/accept', { method: 'POST', body: { token, password } }); window.history.replaceState(null, '', '/invito'); setPassword(''); setMessage('Invito accettato. Puoi accedere con la tua email.'); }
    catch (e) { setMessage(e.message); }
  };
  return <main className="invite-page"><div className="wordmark">AGRINEXT <span>GEOINT</span></div><section><span className="eyebrow">INVITO PERSONALE</span><h1>Attiva l’accesso</h1><p>Imposta una password di almeno 14 caratteri. Il collegamento può essere usato una sola volta.</p><form onSubmit={submit}>{hybridEnabled && <label>Email invitata<input type="email" autoComplete="username" value={email} onChange={e=>setEmail(e.target.value)} required /></label>}<label htmlFor="invite-password">Nuova password</label><input id="invite-password" data-testid="invite-password-input" type="password" minLength={14} value={password} onChange={(e) => setPassword(e.target.value)} required/><button className="action-primary" data-testid="invite-submit-button">Attiva accesso</button></form>{message && <p role="status" data-testid="invite-status">{message}</p>}<a href="/login" data-testid="invite-login-link">Vai all’accesso →</a></section></main>;
}

export default function App() {
  return <BrowserRouter><AuthProvider><Routes>
    <Route path="/login" element={<Login />} /><Route path="/invito" element={<Invite />} /><Route path="/attiva" element={<Invite />} />
    <Route element={<Protected />}><Route path="/progetti" element={<Dashboard />} /><Route path="/progetti/:projectId" element={<Workspace />}>
      <Route index element={<Navigate to="mappa" replace />} /><Route path="motore-locale" element={<LocalEnginePage />} /><Route path="mappa" element={<MapPage />} /><Route path="scheda" element={<ParcelPage />} /><Route path="fonti" element={<SourcesPage />} /><Route path="verifiche" element={<ChecksPage />} /><Route path="pratiche" element={<PracticesPage />} /><Route path="dossier" element={<DossiersPage />} /><Route path="accessi" element={<AccessPage />} />
    </Route></Route><Route path="*" element={<Navigate to="/progetti" replace />} />
  </Routes></AuthProvider></BrowserRouter>;
}
